/**
 * Shared constituent filtering utilities used during registration to
 * normalise and deduplicate Altru search results.
 */

export interface ConstituentCandidate {
  id: string;
  lookupId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  household?: boolean;
  group?: boolean;
}

/** Strip all non-digit characters for loose phone comparison. */
export function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, "");
}

/**
 * Filters a raw list of Altru constituent search results to valid individual
 * matches for the given email + phone pair.
 *
 * Rules applied in order:
 * 1. Discard household and group records (not individual constituents).
 * 2. Require phone to match (digits-only comparison).
 * 3. Require email to match when the API returns it; skip the check when it
 *    is absent (Altru already applied the email filter server-side).
 * 4. Discard results whose lookupId differs from the majority — these are
 *    genuinely different people who happen to share the same phone number.
 *    Multiple records sharing one lookupId are treated as duplicates of the
 *    same person and collapsed to a single match.
 *
 * @returns The filtered list of individual constituents. Callers should treat
 *   length === 0 as "not found", length === 1 (or all same lookupId) as a
 *   unique match, and length > 1 with mixed lookupIds as ambiguous.
 */
export function filterConstituents(
  candidates: ConstituentCandidate[],
  email: string,
  phone: string,
): ConstituentCandidate[] {
  const normalizedPhone = normalizePhone(phone);

  const matched = candidates.filter((c) => {
    if (c.household || c.group) return false;

    const phoneMatch = !!c.phone && normalizePhone(c.phone) === normalizedPhone;
    if (!phoneMatch) return false;

    const emailMatch =
      c.email === undefined || c.email === null
        ? true // not returned by API — trust Altru's server-side filter
        : c.email.toLowerCase() === email.toLowerCase();

    return emailMatch;
  });

  return matched;
}

/**
 * Returns the unique lookup IDs from a filtered constituent list.
 * A size of 1 means all records belong to the same person (safe to proceed).
 * A size > 1 means genuinely different people — ambiguous, contact support.
 */
export function uniqueLookupIds(
  candidates: ConstituentCandidate[],
): Set<string> {
  return new Set(candidates.map((c) => c.lookupId).filter(Boolean) as string[]);
}

/**
 * Returns the number of unique individual constituents in a raw result list.
 * Excludes household/group records and deduplicates by lookup ID so that
 * multiple address records for the same person count as 1.
 * Use this wherever constituent count is needed — do not inline the logic.
 */
export function countUniqueConstituents(
  candidates: ConstituentCandidate[],
): number {
  const individuals = candidates.filter((c) => !c.household && !c.group);
  return uniqueLookupIds(individuals).size;
}

/**
 * Further narrows an already-filtered constituent list by first and last name.
 * Comparison is case-insensitive and trims whitespace.
 * An empty/missing name component is treated as a wildcard (no filter applied for that field).
 */
export function filterConstituentsByName(
  candidates: ConstituentCandidate[],
  firstName: string,
  lastName: string,
): ConstituentCandidate[] {
  const normFirst = firstName.trim().toLowerCase();
  const normLast = lastName.trim().toLowerCase();
  return candidates.filter((c) => {
    const matchFirst = !normFirst || (c.firstName ?? "").trim().toLowerCase() === normFirst;
    const matchLast = !normLast || (c.lastName ?? "").trim().toLowerCase() === normLast;
    return matchFirst && matchLast;
  });
}
